<template>
  <q-page v-if="data">
    <PageBreadcrumbs
      :items="[{ label: 'Blog', to: '/blog' }, { label: article.title }]"
    />

    <div
      class="q-img"
      style="height: 350px"
    >
      <NuxtImg
        :preload="{ fetchPriority: 'high' }"
        :src="bannerSrc"
        provider="cloudflare"
        height="350"
        fit="crop"
        class="fit"
        sizes="xs:400px sm:600px md:800px lg:1600px"
        style="object-fit: cover; object-position: 50% 50%;"
      />
      <div class="q-img__content absolute-full q-anchor--skip">
        <div class="fit column flex-center overlay q-px-md">
          <h1 class="text-h2 text-weight-bold text-center q-my-md">
            {{ article.title }}
          </h1>
          <em class="text-subtitle2">
            Published on
            <strong>{{ format(article.publishedAt, 'MMMM D, YYYY') }}</strong>
            <span>
              by <strong>{{ article.author ?? 'KYGUNCO Staff' }}</strong>
            </span>
          </em>
          <div class="q-my-md">
            <q-btn
              :icon="mdiShareVariant"
              label="Share"
              square
              flat
              @click="shareDialog()"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="q-pa-md">
      <StrapiBlocks
        :nodes="article.content"
        class="text-content"
        anchors
      />

      <div
        v-if="article.videos?.data?.length"
        class="q-pt-md"
      >
        <div class="row justify-between items-center q-pb-md">
          <h2 class="text-h5 text-weight-medium no-margin">
            Related Videos
          </h2>
        </div>

        <q-list
          bordered
          separator
        >
          <q-item
            v-for="video in article.videos.data"
            :key="video.id"
            :to="`/video/${video.attributes.slug}`"
          >
            <q-item-section avatar>
              <q-img
                :src="video.attributes.thumbnail.data.attributes.formats?.medium.url"
                ratio="1.7777"
                width="100px"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label lines="2">
                {{ video.attributes.title }}
              </q-item-label>
              <q-item-label caption>
                Published {{ relative(video.attributes.publishedAt) }}
              </q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-icon
                :name="mdiPlay"
                color="primary"
                size="md"
              />
            </q-item-section>
          </q-item>
        </q-list>
      </div>

      <LazyCatalogCarousel
        v-for="catalog in article.catalog ?? []"
        :key="catalog.id"
        :query="catalog.query ?? undefined"
        :size="catalog.size ?? 12"
        :search="catalog.search"
        :name="catalog.heading"
        :data-key="`articles_${data.id}_catalog_${catalog.id}_products`"
        class="q-pt-md"
      />
    </div>
  </q-page>
</template>

<script lang="ts" setup>
import { mdiShareVariant, mdiPlay } from '@quasar/extras/mdi-v7';
import { withoutHost } from 'ufo';
import type { Article } from '~/types/strapi';

const route = useRoute();
const { format, relative } = useDateFormat();
const { shareDialog } = useDialogs();
const { find } = useStrapi();

const secret = process.env.NUXT_STRAPI_PREVIEW_SECRET ?? '';

const { data, error } = await useAsyncData(
  `articles_${route.params.slug}`,
  () =>
    find<Article>('articles', {
      filters: {
        slug: {
          $eq: route.params.slug,
        },
      },
      publicationState: secret === route.query.preview ? 'preview' : 'live',
      populate: [
        'seo.metaImage',
        'banner',
        'catalog',
        'image',
        'videos.thumbnail',
      ],
    }),
  {
    transform: response => (response.data.length ? response.data[0] : null),
  },
);

if (error.value) {
  if (import.meta.server) {
    throw createError({ ...error.value });
  }

  throw showError({ ...error.value });
}

if (!data.value) {
  if (import.meta.server) {
    throw createError({
      message: 'Post not found',
      statusCode: 404,
    });
  }

  throw showError({
    message: 'Post not found',
    statusCode: 404,
  });
}

const article = data.value.attributes;

const bannerSrc = computed(
  () =>
    article.banner?.data?.attributes?.url
      ? withoutHost(article.banner?.data?.attributes?.url)
      : '/assets/splash/default-content.jpg',
);

defineOgImage({
  url:
    article.seo?.metaImage?.data?.attributes?.url
    ?? article.image?.data?.attributes?.url,
});

useSeoMeta({
  title: () => article.seo?.metaTitle ?? article.title,
  titleTemplate: '%s %separator Blog %separator %siteName',
  description: () => article.seo?.metaDescription,
  robots: () => article.seo?.metaRobots,
  articleAuthor: () => [article.author ?? 'KYGUNCO Staff'],
  articlePublishedTime: () => article.publishedAt,
  articleModifiedTime: () => article.updatedAt,
});

useSchemaOrg(defineSchemaOrgArticle(article));
</script>
